import clsx from 'clsx';
import React, { useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useVerifyIdentity } from '@/modules/workforce/workersTimesheet/apiHooks';
import { IWorkersTimesheet } from '@/modules/workforce/workersTimesheet/types';
import { Button, Typography } from '@/shared/components/atoms';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Icon from '~/images/shared/verify-identity.svg';
import WarningIcon from '~/images/shared/warning.svg';

export const ConfirmVerifyIdentity = ({
  date,
  workerId,
  attendanceActionType,
  verifyPP,
}: IWorkersTimesheet.IConfirmVerifyProps) => {
  const { t } = useTranslation('common');
  const { closeModal } = useModal();
  const [selectedAsset] = useStore((store) => store?.asset);
  const isFraud = useMemo(
    () =>
      attendanceActionType === 'fraud-worker-attendance' ||
      attendanceActionType === 'fraud-worker-dismissal',
    [attendanceActionType]
  );
  /********************************* api *************************************** */
  const { mutate, isLoading } = useVerifyIdentity({
    attendanceActionType,
  });
  /********************************* logic *************************************** */
  const handleVerifyIdentity = React.useCallback(() => {
    mutate(
      {
        date,
        entityId: selectedAsset?.id,
        workerId,
      },
      {
        onSuccess: () => {
          toast.success(t('verify-identity-successfully'));
          closeModal(2);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeModal, date, mutate, selectedAsset?.id, workerId]);

  return (
    <div className='flex w-412  flex-col items-center gap-16 text-center'>
      <span
        className={clsx(
          'mb-16 rounded-full bg-secondary-90 p-12 text-40',
          isFraud && 'bg-error-90'
        )}
      >
        {isFraud ? <WarningIcon /> : <Icon />}
      </span>

      <Typography variant='h3' weight='semiBold' className='text-center'>
        {t(isFraud ? 'confirm-as-fraud' : 'confirm-verification')}
      </Typography>
      <Typography variant='body1' className='text-center'>
        {t(
          isFraud
            ? 'confirm-fraud-identity-description'
            : verifyPP
            ? 'confirm-face-verify-identity-description'
            : 'confirm-verify-identity-description'
        )}
      </Typography>
      <div className='mt-16 flex w-full items-center justify-between gap-16'>
        <Button
          variant='filled'
          className='w-full'
          size='lg'
          color='secondary'
          onClick={() => closeModal()}
        >
          {t('cancel-btn')}
        </Button>
        <Button
          className='w-full'
          size='lg'
          loading={isLoading}
          onClick={handleVerifyIdentity}
        >
          {t(isFraud ? 'confirm' : 'verify-btn')}
        </Button>
      </div>
    </div>
  );
};
